import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import useSWRInfinite from 'swr/infinite';
import { TextField } from '@/components/TextField';
import { Thumbnail } from '@/components/Thumbnail';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { useDebounce, useInfiniteScroll } from '@/hooks';
import { EngineService, ThumbnailSize } from '@/services/engine';
import type { PaginatedResponse } from '@/types/responses';
import { type FullSource, type SourceType } from '@/types/source';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

export interface SourceSelectionGridProps {
    selectedSourceId: string | null;
    onSelect: (source: FullSource) => void;
    scrollContainer: HTMLElement | null;
    searchPlaceholder: string;
    emptyLabel: string;
    loadingLabel: string;
    cacheScope: string;
    type?: SourceType;
}

export function SourceSelectionGrid({
    selectedSourceId,
    onSelect,
    scrollContainer,
    searchPlaceholder,
    emptyLabel,
    loadingLabel,
    cacheScope,
    type,
}: SourceSelectionGridProps) {
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

    const { data, size, setSize, isLoading, isValidating } = useSWRInfinite(
        (pageIndex, previous: PaginatedResponse<FullSource> | null) => {
            if (previous && previous.items.length === 0) return null;
            return [
                CACHE_KEYS.SOURCES_PREFIX,
                cacheScope,
                type ?? 'all',
                debouncedSearch,
                pageIndex + 1,
            ] as const;
        },
        ([, , sourceType, search, page]) =>
            EngineService.getSources({
                type: sourceType === 'all' ? undefined : sourceType,
                page,
                size: PAGE_SIZE,
                search: search || undefined,
                orderBy: 'createdAt desc',
            }),
        { revalidateFirstPage: false },
    );

    const sources = useMemo<FullSource[]>(
        () => (data ? data.flatMap((page) => page.items) : []),
        [data],
    );
    const totalItems = data?.[0]?.page.totalItems ?? 0;
    const hasMore = sources.length < totalItems;

    useInfiniteScroll(scrollContainer, {
        onLoadMore: () => {
            if (!isValidating && hasMore) void setSize(size + 1);
        },
        isLoading: isValidating,
        hasMore,
    });

    const showEmptyState = !isLoading && !isValidating && sources.length === 0;

    return (
        <div className="flex flex-col gap-6 p-6">
            <TextField
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                leadingIcon={<MagnifyingGlassIcon className="size-4" />}
                wrapperClassName="rounded-none border-x-0 border-t-0 bg-transparent px-0"
            />
            {showEmptyState ? (
                <div className="flex h-40 items-center justify-center text-sm text-grey-200">
                    {emptyLabel}
                </div>
            ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3">
                    {sources.map((source) => (
                        <Thumbnail
                            key={source.id}
                            imageSrc={EngineService.getSourceThumbnailUrl(
                                source.id,
                                ThumbnailSize.MQ,
                            )}
                            title={source.displayName || source.name}
                            checked={selectedSourceId === source.id}
                            showCheckbox
                            showBorder
                            showGradients
                            onClick={() => onSelect(source)}
                        />
                    ))}
                </div>
            )}
            {isValidating ? (
                <div className="flex h-10 items-center justify-center text-sm text-grey-300">
                    {loadingLabel}
                </div>
            ) : null}
        </div>
    );
}
